/**
 * Registration Component
 * Provides secure registration form for customs brokers
 * Includes real-time domain validation and password strength checking
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Icon,
  Image,
  Link,
  InputGroup,
  InputRightElement,
  IconButton,
  Progress,
  List,
  ListItem,
  ListIcon,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import {
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
} from 'react-icons/fa';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { isValidEmail, evaluatePasswordStrength } from '../utils/security.utils';
import { PasswordStrength } from '../types/auth.types';

interface RegisterProps {}

const Register: React.FC<RegisterProps> = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { register, validateDomain, isLoading } = useAuth();
  const { t } = useLanguage();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Validation state
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);
  
  // Domain validation state
  const [isDomainValidating, setIsDomainValidating] = useState(false);
  const [domainValidated, setDomainValidated] = useState(false);
  const [brokerName, setBrokerName] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<string | null>(null);

  /**
   * Validate email domain in real-time (debounced)
   */
  const validateEmailDomain = useCallback(
    async (emailValue: string) => {
      if (!emailValue || !isValidEmail(emailValue)) {
        setDomainValidated(false);
        setBrokerName(null);
        setDomainError(null);
        return;
      }

      setIsDomainValidating(true);
      setDomainError(null);

      try {
        const result = await validateDomain(emailValue);

        if (result.valid && result.broker) {
          setDomainValidated(true);
          setBrokerName(result.broker.name);
          setDomainError(null);
        } else {
          setDomainValidated(false);
          setBrokerName(null);
          setDomainError(
            result.message || t('register.email_domain_invalid')
          );
        }
      } catch (error) {
        setDomainValidated(false);
        setBrokerName(null);
        setDomainError(t('register.email_validation_error'));
      } finally {
        setIsDomainValidating(false);
      }
    },
    [validateDomain, t]
  );

  /**
   * Debounced email validation
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email) {
        validateEmailDomain(email);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [email, validateEmailDomain]);

  /**
   * Evaluate password strength in real-time
   */
  useEffect(() => {
    if (password) {
      const strength = evaluatePasswordStrength(password);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(null);
    }
  }, [password]);

  /**
   * Clear errors when user starts typing
   */
  useEffect(() => {
    if (email || password || confirmPassword) {
      setErrors({});
    }
  }, [email, password, confirmPassword]);

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
    } = {};

    // Email validation
    if (!email.trim()) {
      newErrors.email = t('register.email_required');
    } else if (!isValidEmail(email)) {
      newErrors.email = t('register.email_invalid');
    } else if (!domainValidated) {
      newErrors.email = domainError || t('register.email_domain_invalid');
    }

    // Password validation
    if (!password) {
      newErrors.password = t('register.password_required');
    } else if (!passwordStrength || !passwordStrength.isStrong) {
      newErrors.password = t('register.password_strength_invalid');
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = t('register.confirm_password_required');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('register.passwords_mismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle registration form submission
   */
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const response = await register(email, password, confirmPassword);

      if (response.success) {
        toast({
          title: t('register.success_title'),
          description: t('register.success_desc'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/bill-of-lading');
        }, 500);
      } else {
        toast({
          title: t('register.failed_title'),
          description: response.error || t('register.failed_desc'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      toast({
        title: t('common.error'),
        description: err.message || t('common.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Get password strength color
   */
  const getPasswordStrengthColor = (): string => {
    if (!passwordStrength) return 'gray';
    if (passwordStrength.score <= 1) return 'red';
    if (passwordStrength.score <= 2) return 'orange';
    if (passwordStrength.score <= 3) return 'yellow';
    return 'green';
  };

  /**
   * Get password strength label
   */
  const getPasswordStrengthLabel = (): string => {
    if (!passwordStrength) return '';
    if (passwordStrength.score <= 1) return t('register.strength.weak');
    if (passwordStrength.score <= 2) return t('register.strength.fair');
    if (passwordStrength.score <= 3) return t('register.strength.good');
    return t('register.strength.strong');
  };

  return (
    <Box
      minH="100vh"
      bg="appleBg"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Box maxW="400px" w="full" py={16}>
        <form onSubmit={handleRegister}>
          <VStack spacing={8}>
            {/* Header */}
            <VStack spacing={4} textAlign="center">
              <Image src="/BRL_Blue_Squared.png" alt="Logo" boxSize={10} />
              <Heading fontSize="28px" fontWeight="600" lineHeight="1.14" color="appleTextPrimary">
                {t('register.title')}
              </Heading>
              <Text color="appleTextSecondary" fontSize="sm">
                {t('register.subtitle')}
              </Text>
            </VStack>

            {/* Broker Validation Success */}
            {domainValidated && brokerName && (
              <Alert status="success" variant="subtle" borderRadius="sm" w="full">
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('register.domain_verified')}</AlertTitle>
                  <AlertDescription>
                    <HStack>
                      <Icon as={FaBuilding} />
                      <Text>{brokerName}</Text>
                    </HStack>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {/* Form Fields */}
            <VStack spacing={6} w="full">
              {/* Email */}
              <FormControl isInvalid={!!errors.email || !!domainError} isRequired>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('register.email_label')}
                </FormLabel>
                <InputGroup>
                  <Input
                    variant="flushed"
                    type="email"
                    placeholder={t('register.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    size="lg"
                    autoComplete="email"
                    aria-label="Email address"
                    focusBorderColor="appleAccent"
                    borderColor="appleCardBorder"
                  />
                  <InputRightElement h="full">
                    {isDomainValidating ? (
                      <Spinner size="sm" color="appleAccent" />
                    ) : domainValidated ? (
                      <Icon as={FaCheckCircle} color="green.500" />
                    ) : domainError && email ? (
                      <Icon as={FaTimesCircle} color="red.500" />
                    ) : null}
                  </InputRightElement>
                </InputGroup>
                <FormHelperText fontSize="xs">
                  {t('register.email_helper')}
                </FormHelperText>
                <FormErrorMessage>{errors.email || domainError}</FormErrorMessage>
              </FormControl>

              {/* Password */}
              <FormControl isInvalid={!!errors.password} isRequired>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('register.password_label')}
                </FormLabel>
                <InputGroup>
                  <Input
                    variant="flushed"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('register.password_placeholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    size="lg"
                    autoComplete="new-password"
                    aria-label="Password"
                    focusBorderColor="appleAccent"
                    borderColor="appleCardBorder"
                  />
                    <InputRightElement h="full">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="sm"
                      />
                    </InputRightElement>
                  </InputGroup>
                  
                  {/* Password Strength Indicator */}
                  {password && passwordStrength && (
                    <Box mt={2}>
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" color="appleTextSecondary">
                          {t('register.password_strength')}
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={`${getPasswordStrengthColor()}.500`}
                        >
                          {getPasswordStrengthLabel()}
                        </Text>
                      </HStack>
                      <Progress
                        value={(passwordStrength.score / 4) * 100}
                        size="sm"
                        colorScheme={getPasswordStrengthColor()}
                        borderRadius="md"
                      />
                    </Box>
                  )}
                  
                  {/* Password Requirements */}
                  {password && passwordStrength && (
                    <Box mt={2} p={2} bg="appleSurface" borderRadius="sm">
                      <Text fontSize="xs" fontWeight="bold" mb={1}>
                        {t('register.requirements')}
                      </Text>
                      <List spacing={1}>
                        <ListItem fontSize="xs">
                          <ListIcon
                            as={passwordStrength.requirements.minLength ? FaCheckCircle : FaTimesCircle}
                            color={passwordStrength.requirements.minLength ? 'green.500' : 'red.500'}
                          />
                          {t('register.req.min_length')}
                        </ListItem>
                        <ListItem fontSize="xs">
                          <ListIcon
                            as={passwordStrength.requirements.hasUppercase ? FaCheckCircle : FaTimesCircle}
                            color={passwordStrength.requirements.hasUppercase ? 'green.500' : 'red.500'}
                          />
                          {t('register.req.uppercase')}
                        </ListItem>
                        <ListItem fontSize="xs">
                          <ListIcon
                            as={passwordStrength.requirements.hasLowercase ? FaCheckCircle : FaTimesCircle}
                            color={passwordStrength.requirements.hasLowercase ? 'green.500' : 'red.500'}
                          />
                          {t('register.req.lowercase')}
                        </ListItem>
                        <ListItem fontSize="xs">
                          <ListIcon
                            as={passwordStrength.requirements.hasNumber ? FaCheckCircle : FaTimesCircle}
                            color={passwordStrength.requirements.hasNumber ? 'green.500' : 'red.500'}
                          />
                          {t('register.req.number')}
                        </ListItem>
                        <ListItem fontSize="xs">
                          <ListIcon
                            as={passwordStrength.requirements.hasSpecialChar ? FaCheckCircle : FaTimesCircle}
                            color={passwordStrength.requirements.hasSpecialChar ? 'green.500' : 'red.500'}
                          />
                          {t('register.req.special')}
                        </ListItem>
                      </List>
                    </Box>
                  )}
                  
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>

              {/* Confirm Password */}
              <FormControl isInvalid={!!errors.confirmPassword} isRequired>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('register.confirm_password_label')}
                </FormLabel>
                <InputGroup>
                  <Input
                    variant="flushed"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('register.confirm_password_placeholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    size="lg"
                    autoComplete="new-password"
                    aria-label="Confirm password"
                    focusBorderColor="appleAccent"
                    borderColor="appleCardBorder"
                  />
                  <InputRightElement h="full">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      icon={<Icon as={showConfirmPassword ? FaEyeSlash : FaEye} />}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
                {confirmPassword && password === confirmPassword && (
                  <FormHelperText fontSize="xs" color="green.500">
                    <Icon as={FaCheckCircle} mr={1} />
                    {t('register.passwords_match')}
                  </FormHelperText>
                )}
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>
            </VStack>

            {/* Register Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              w="full"
              borderRadius="full"
              isLoading={isLoading}
              isDisabled={!domainValidated || isDomainValidating}
              loadingText={t('register.creating')}
            >
              {t('register.create_btn')}
            </Button>

            {/* Login Link */}
            <VStack spacing={3}>
              <Text fontSize="sm" color="appleTextSecondary">
                {t('register.already_have_account')}
              </Text>
              <Link
                as={RouterLink}
                to="/login"
                fontSize="sm"
                fontWeight="medium"
                color="appleAccent"
              >
                {t('register.sign_in_btn')}
              </Link>
            </VStack>

            {/* Info Box */}
            <Box
              bg="appleSurface"
              p={3}
              borderRadius="sm"
              w="full"
              borderWidth="1px"
              borderColor="appleCardBorder"
            >
              <Text fontSize="xs" color="appleTextSecondary">
                <strong>Note:</strong> {t('register.note')}
              </Text>
            </Box>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default Register;
